"use client";

import Link from "next/link";
import { useState, memo } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import useProductDetail from "@/app/lib/features/productDetail/productDetail.hook";
import { Button } from "../../shadcn/button";
import FallbackImage from "../../elements/FallbackImage";
import { trackInteraction } from "@/app/lib/tracking/interactions";
import { useTrackGenericView } from "@/app/hooks/useTrackView";

const ProductAboutSection = memo(() => {
  const { product } = useProductDetail();
  const [isExpanded, setIsExpanded] = useState(false);
  const trackRef = useTrackGenericView("product_about_section");

  if (!product) return null;

  const listItems =
    product.store_info_group?.details?.map((detail) => {
      if (detail.includes("[]") && product.store_info_group?.seller_name) {
        const parts = detail.split("[]");
        return {
          text: (
            <>
              {parts[0]}
              <Link
                href={`/magaza/${product.store_info_group.seller_slug || ""}`}
                onClick={() => trackInteraction("seller", product.store_info_group!.seller_id, "click")}
                className="text-black underline font-medium"
              >
                {product.store_info_group.seller_name}
              </Link>
              {parts[1]}
            </>
          ),
        };
      }
      return { text: detail };
    }) || [];

  const specifications = product.specifications?.attributes || [];
  const selectedColor = product.variants_by_color?.[0];

  return (
    <section ref={trackRef} className="space-y-4 sm:space-y-6" id="product-information">
      <h2 className="text-base sm:text-xl font-medium my-3">Ürün Bilgileri</h2>
      <div className="border rounded-md p-3 sm:p-5">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-6">
          <div className="lg:col-span-3 xl:col-span-2 flex justify-center lg:justify-start">
            <div className="relative w-30 sm:w-35 lg:w-41.25 h-45 sm:h-52.5 lg:h-61.75">
              <FallbackImage
                src={product.images[0]}
                alt={product.internet_product_description}
                fill
                className="object-cover rounded-md"
                sizes="(max-width: 640px) 120px, (max-width: 1024px) 140px, 165px"
                quality={70}
                loading="lazy"
              />
            </div>
          </div>
          <div className="lg:col-span-5 space-y-2">
            <h3 className="text-base sm:text-lg font-medium">
              Ürün Açıklaması
            </h3>
            <div className="space-y-2 text-xs sm:text-sm text-[#666]">
              <p>{product.internet_product_description}</p>
              <p>
                {product.product_description ||
                  product.internet_product_description}
              </p>
            </div>
          </div>
          <div className="lg:col-span-4 xl:col-span-5 space-y-2">
            <h3 className="text-base sm:text-lg font-medium">Ek Bilgiler</h3>
            <div className="relative">
              <div
                className={`transition-all duration-500 ease-in-out overflow-hidden ${
                  isExpanded ? "max-h-none" : "max-h-37.5 sm:max-h-50"
                } pl-1`}
              >
                <ul className="list-disc ml-4 space-y-1 text-xs sm:text-sm my-4 marker:text-black">
                  {listItems.map((item, index) => (
                    <li key={index} className="text-muted-foreground">
                      {item.text}
                    </li>
                  ))}
                </ul>
              </div>
              <div
                className={`absolute bottom-0 left-0 right-0 h-8 sm:h-12 bg-linear-to-t from-white to-transparent pointer-events-none transition-opacity duration-300 ${
                  isExpanded ? "opacity-0" : "opacity-100"
                }`}
              />
            </div>
          </div>
        </div>
        <div className="relative flex items-center justify-center mt-4">
          <Button
            variant="outline"
            className="rounded-full px-6 sm:px-10"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? "DAHA AZ GÖSTER" : "DAHA FAZLA GÖSTER"}
            {isExpanded ? (
              <ChevronUp size={18} className="sm:w-5 sm:h-5" />
            ) : (
              <ChevronDown size={18} className="sm:w-5 sm:h-5" />
            )}
          </Button>
          <div className="absolute left-0 top-1/2 translate-y-1/2 w-full h-px bg-[#e5e5e5] -z-10" />
        </div>
        {(specifications.length > 0 ||
          product.brand_name ||
          selectedColor ||
          product.general_sizes?.length > 0) && (
          <div className="space-y-3">
            <h3 className="text-base sm:text-lg font-medium my-3">
              {product.specifications?.title || "Ürün Özellikleri"}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
              {specifications.map((spec, index) => (
                <div
                  key={index}
                  className="bg-[#f5f5f5] rounded-md p-3 sm:p-4 flex items-center justify-between text-sm"
                >
                  <p>{spec.label}</p>
                  <p className="font-medium">{spec.value}</p>
                </div>
              ))}
            </div>
          </div>
        )}

              </div>
    </section>
  );
});

ProductAboutSection.displayName = "ProductAboutSection";

export default ProductAboutSection;
